import { mskDate, mskDayMonth, mskTime } from '../ui/moscow';
import { форматЧисла } from '../ui/chisla';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * СЛОВА КАРТОЧКИ КАБИНЕТА НА ЭКРАНЕ ВЫБОРА.
 *
 * Эталон пишет на карточках три разные метки: «выгружен 4 минуты
 * назад» зелёным, «выгружен вчера в 22:10» серым, «ни разу
 * не синхронизировался» жёлтым. Это не украшение: человек с тремя
 * кабинетами выбирает не по имени — имена он и так знает, — а по
 * тому, где что-то не так.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ПОЧЕМУ ЭТО СЧИТАЕТСЯ ЗДЕСЬ, А НЕ НА СЕРВЕРЕ
 *
 * «Четыре минуты назад» стареет прямо на открытой странице: вкладку
 * с кабинетом держат открытой днями. Строка, посчитанная сервером
 * при загрузке, к вечеру говорила бы «четыре минуты назад» о том,
 * что было утром.
 *
 * Состояние ключа — наоборот, приходит готовой строкой с сервера:
 * оно не зависит от времени показа, зато зависит от того, что
 * ответил Wildberries.
 * ═══════════════════════════════════════════════════════════════════════
 */

export type ТонМетки = 'свежо' | 'обычно' | 'тревога';

export interface МеткаВыгрузки {
  текст: string;
  тон: ТонМетки;
}

/** Свежее этого — «только что»: чаще выгрузка и не ходит. */
const СВЕЖО_МИН = 60;

/**
 * Дольше этого молчания — тревога.
 *
 * РАСПИСАНИЕ ХОДИТ РАЗ В ШЕСТЬ ЧАСОВ, и сутки тишины означают, что
 * четыре прогона подряд не дошли. Это уже не «давно», а «сломалось»:
 * ставки при этом ставятся по вчерашним числам.
 */
const ТРЕВОГА_ЧАС = 24;

export function меткаВыгрузки(
  lastSyncAt: string | null,
  сейчас: Date = new Date(),
): МеткаВыгрузки {
  /**
   * НИ РАЗУ — ЭТО НЕ «ДАВНО», А ОТДЕЛЬНЫЙ СЛУЧАЙ, и он тревожный.
   * Кабинет, который ни разу не выгрузился, обычно подключён ключом
   * без прав: смотреть в нём нечего, и сказать об этом надо здесь,
   * а не после того, как человек откроет пустую таблицу.
   */
  if (lastSyncAt === null) return { текст: 'ни разу не синхронизировался', тон: 'тревога' };

  const когда = new Date(lastSyncAt);
  if (Number.isNaN(когда.getTime())) {
    return { текст: 'время выгрузки не разобрано', тон: 'тревога' };
  }

  const минут = Math.floor((сейчас.getTime() - когда.getTime()) / 60_000);

  /* ВПЕРЁД ВРЕМЯ НЕ ИДЁТ. Часы на сервере и в браузере расходятся
     на минуты; «выгружен −3 минуты назад» читается как поломка. */
  if (минут < 1) return { текст: 'выгружен только что', тон: 'свежо' };

  if (минут < СВЕЖО_МИН) {
    return { текст: `выгружен ${минут} ${склонениеМинут(минут)} назад`, тон: 'свежо' };
  }

  const тон: ТонМетки = минут >= ТРЕВОГА_ЧАС * 60 ? 'тревога' : 'обычно';
  const сутки = сутокНазад(когда, сейчас);

  if (сутки === 0) return { текст: `выгружен сегодня в ${mskTime(когда)}`, тон };
  if (сутки === 1) return { текст: `выгружен вчера в ${mskTime(когда)}`, тон };
  return { текст: `выгружен ${mskDayMonth(когда)}`, тон };
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * КОРОТКАЯ СТРОКА О КЛЮЧЕ — «ключ работает · до 12.02.2027».
 *
 * 08.09.2026 владелец про раздел кабинетов: «как будто на пол пути
 * кто-то делал, бросил и забыл». Пропала именно эта строка: эталон
 * пишет её на КАЖДОЙ карточке, а у нас исправные кабинеты молчали.
 *
 * ПОЧЕМУ МОЛЧАЛИ. Карточка печатала `token.text` службы, а служба
 * у здорового ключа отдаёт `null` — и она права: её `text` это АБЗАЦ
 * для беды («Срок ключа Wildberries истёк. Биддер не меняет ставки…»),
 * а у исправного ключа абзаца нет. Ошибка была не в службе, а в том,
 * что карточка спрашивала у неё не то: ей нужна не беда, а состояние.
 *
 * ЧЕЛОВЕК С ТРЕМЯ КАБИНЕТАМИ ВЫБИРАЕТ НЕ ПО ИМЕНИ. Имена он знает;
 * он ищет тот, где что-то не так. Молчание об исправном ключе лишает
 * его сравнения: у одной карточки строка есть, у другой нет — и это
 * читается как поломка вёрстки, а не как «здесь всё хорошо».
 *
 * СЧИТАЕТСЯ ЗДЕСЬ, А НЕ ПРОСИТСЯ У СЛУЖБЫ. `health` и `expiresAt`
 * у карточки уже есть; заводить ради этой строки третье поле в чужом
 * ответе значит ждать соседнюю сессию ради того, что складывается
 * из двух своих.
 * ═══════════════════════════════════════════════════════════════════════
 */
export type ЗдоровьеКлюча = 'ok' | 'none' | 'expired' | 'rejected' | 'soon';

export function короткоОКлюче(
  здоровье: ЗдоровьеКлюча,
  срок: string | null,
  сейчас: Date = new Date(),
): string | null {
  switch (здоровье) {
    case 'none':
      return 'ключа нет — подключите';
    case 'expired':
      return 'срок ключа истёк — обновите';
    case 'rejected':
      return 'ключ не отвечает — обновите';
    case 'soon': {
      const дней = сутокДо(срок, сейчас);
      /* СРОК У «СКОРО» ЕСТЬ ВСЕГДА — иначе службе неоткуда взять это
         состояние. Но `null` тут возможен по типу, и «кончится через
         null дней» хуже, чем просто «кончается». */
      if (дней === null) return 'ключ скоро кончится';
      if (дней <= 0) return 'ключ кончается сегодня';
      return `ключ кончится через ${дней} ${склонениеСуток(дней)}`;
    }
    case 'ok': {
      const дата = датойЧисла(срок);
      /* БЕЗ СРОКА — ПРОСТО «РАБОТАЕТ». Wildberries не у всякого ключа
         отдаёт дату конца, и «работает · до » с пустотой после
         предлога выглядит обрывом строки. */
      return дата === null ? 'ключ работает' : `ключ работает · до ${дата}`;
    }
  }
}

/**
 * «12.02.2027» — цифрами: строка стоит в ряду карточек, места мало.
 *
 * ЧЕРЕЗ ОБЩЕГО ПОМОЩНИКА (`mskDate`), А НЕ СВОИМ ВЫЗОВОМ РАСКЛАДКИ.
 * Свой сразу поймали два сторожа — `ui/moscow.spec.ts`
 * и `ui/chisla.spec.ts`, — и поймали за дело: часовой пояс и раскладка
 * задавались бы здесь заново, а «Europe/Moscow», написанный второй
 * раз, однажды разойдётся с первым. Весь кабинет живёт по московскому
 * времени одним местом.
 */
function датойЧисла(iso: string | null): string | null {
  if (iso === null || iso === '') return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return mskDate(d);
}

function сутокДо(iso: string | null, сейчас: Date): number | null {
  if (iso === null || iso === '') return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((d.getTime() - сейчас.getTime()) / (24 * 3600 * 1000));
}

/** «день · дня · дней» — та же тройка, что у остатка товара. */
function склонениеСуток(n: number): string {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return 'дней';
  if (b > 1 && b < 5) return 'дня';
  if (b === 1) return 'день';
  return 'дней';
}

/**
 * Знак в квадратике: первая буква имени.
 *
 * ИМЕНИ МОЖЕТ НЕ БЫТЬ ВОВСЕ — тогда вопрос, как в эталоне. Подставлять
 * туда букву из номера кабинета или из внутреннего опознавателя значило
 * бы нарисовать три одинаковых квадратика с «7» и «a».
 */
/**
 * ═══════════════════════════════════════════════════════════════════════
 * ПРАВОВАЯ ФОРМА — НЕ ИМЯ, И БУКВУ ЕЙ НЕ ОТДАЁМ.
 *
 * Отдавали: «ООО «Медуза Сильвер»» давало «О», «ИП Ким А. В.» — «И».
 * В списке из пяти кабинетов у трёх кружок выходил с буквой «О», ещё
 * у двух с «И», и кружок переставал что-либо различать — то есть
 * переставал быть кружком с буквой и становился украшением.
 *
 * В эталоне те же два кабинета несут «М» и «К»: буква берётся
 * от НАЗВАНИЯ, а «ООО» и «ИП» пропускаются.
 *
 * СПИСОК ЗАКРЫТЫЙ И КОРОТКИЙ. Это не разбор юридических форм,
 * а пять сокращений, которые встречаются у продавцов Wildberries.
 * Незнакомое слово остаётся именем: «Медуза» лучше пропустить как
 * форму по ошибке нельзя, а «АО» в списке.
 * ═══════════════════════════════════════════════════════════════════════
 */
const ФОРМЫ = new Set(['ооо', 'оао', 'зао', 'ао', 'ип', 'пао', 'нко', 'сзи', 'кфх']);

export function знакКабинета(имя: string | null): string {
  const чистое = (имя ?? '').trim();
  if (чистое === '') return '?';

  /* Кавычки и скобки в начале — не буква: «ООО «Медуза Сильвер»»
     должно дать «М», а `«Медуза»` — «М», а не ««». */
  const слова = чистое.split(/[\s«»"'(),.]+/u).filter((с) => с !== '');
  const имяБезФормы = слова.find((с) => !ФОРМЫ.has(с.toLowerCase()));

  const буква = (имяБезФормы ?? чистое).match(/\p{L}|\p{N}/u);
  return (буква?.[0] ?? '?').toUpperCase();
}

/**
 * «кабинет WB 1 402 887» — с неразрывными пробелами по три знака.
 *
 * НОМЕР ЧИТАЮТ ГЛАЗАМИ И ДИКТУЮТ В ПОДДЕРЖКУ. Семизначное число
 * без разбивки на группы читается по одной цифре и переписывается
 * с ошибкой; так же его показывает и сам Wildberries.
 */
export function номерКабинета(wbOwnerId: number | null): string | null {
  if (wbOwnerId === null) return null;
  return `кабинет WB ${форматЧисла().format(wbOwnerId).replace(/ /g, ' ')}`;
}

/**
 * «ДРР до 18 % · заказ до 428 ₽» — как в эталоне.
 *
 * ЦЕНЫ ЗАКАЗА МОЖЕТ НЕ БЫТЬ: в режиме «из ДРР» её считают из среднего
 * чека, а чека нет, пока не было заказов. Тогда строка обрывается
 * на пределе ДРР — и это честнее, чем «заказ до 0 ₽».
 */
export function строкаЦелей(цели: { drr: number; цена: number | null }): string {
  const дрр = `ДРР до ${цели.drr} %`;
  if (цели.цена === null) return дрр;
  return `${дрр} · заказ до ${форматЧисла().format(цели.цена).replace(/ /g, ' ')} ₽`;
}

function склонениеМинут(n: number): string {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return 'минут';
  if (b > 1 && b < 5) return 'минуты';
  if (b === 1) return 'минуту';
  return 'минут';
}

/**
 * Сколько КАЛЕНДАРНЫХ суток назад это было — по московским, а не по
 * своим: кабинет живёт в московском времени, и «вчера в 22:10»
 * у человека из Владивостока должно значить то же самое, что
 * у человека из Калининграда.
 */
function сутокНазад(когда: Date, сейчас: Date): number {
  const день = (d: Date) => mskDayMonth(d) + ' ' + d.getUTCFullYear();
  if (день(когда) === день(сейчас)) return 0;
  const вчера = new Date(сейчас.getTime() - 24 * 3600 * 1000);
  if (день(когда) === день(вчера)) return 1;
  return 2;
}
